import { createEffect, createSignal, onCleanup, type Accessor } from "solid-js";
import { driverCardBackUrl } from "~/lib/svg-assets";
import { gcOrphans, getCardBgUrl, prewarmCardBg } from "~/lib/card-bg-cache";

export interface CardBackground {
  prefix: string;
  variations: number;
}

// Derived at build time from the SVG asset folders under
// `src/assets/driver-card/back/<prefix>/NN.svg`. The convention is that each
// style's variations are numbered sequentially starting at 00.svg, and the
// file count equals the value of `variations`. Adding a new style is "create
// a folder and drop SVGs in it"; adding a variation is "drop another file."
// Lexical sort by prefix gives a deterministic, OS-independent order.
const _backFiles = import.meta.glob<string>(
  "/src/assets/driver-card/back/**/*.svg",
  { query: "?url", import: "default", eager: true },
);

function buildCardBackgrounds(): CardBackground[] {
  const counts = new Map<string, number>();
  for (const key of Object.keys(_backFiles)) {
    const m = /\/back\/([^/]+)\/\d+\.svg$/.exec(key);
    if (!m) continue;
    counts.set(m[1], (counts.get(m[1]) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([prefix, variations]) => ({ prefix, variations }));
}

export const CARD_BACKGROUNDS: CardBackground[] = buildCardBackgrounds();

export const CARD_BACKGROUND_MAP = new Map<string, CardBackground>(
  CARD_BACKGROUNDS.map((b) => [b.prefix, b]),
);

interface AssignedBackground {
  bg: CardBackground;
  variation: number;
}

const FLAT: AssignedBackground[] = CARD_BACKGROUNDS.flatMap((bg) =>
  Array.from({ length: bg.variations }, (_, variation) => ({ bg, variation })),
);

// Additive offset applied after the multiplication. Bumping this rotates every
// user's assignment by N slots without breaking the bijection. Use it to
// reshuffle by hand if the current arrangement doesn't aesthetically land.
const SHUFFLE_SALT = 10;

function gcd(a: number, b: number): number {
  while (b) [a, b] = [b, a % b];
  return a;
}

// Multiplicative permutation over FLAT. P must be coprime with FLAT.length so
// that `driverNumber * P mod FLAT.length` is a bijection on 0..FLAT.length-1,
// giving zero background collisions for the first FLAT.length drivers.
// Walks up from 137 to the nearest coprime so a catalog size of 137, 274, etc.
// can't silently degrade the shuffle and never throws at boot.
const PERMUTATION_P = (() => {
  let p = 137;
  while (gcd(p, FLAT.length) !== 1) p++;
  return p;
})();

export function pickAssignedBackground(
  driverNumber: number,
): AssignedBackground {
  return FLAT[(driverNumber * PERMUTATION_P + SHUFFLE_SALT) % FLAT.length];
}

// Resolves the URL for `requested` if its asset exists, otherwise falls back
// to the driver's bijection-assigned background. The override path (future)
// calls this with the user's chosen bg; non-override callers pass the
// assigned bg directly, in which case the fallback step is a no-op. Empty
// string at the very end guards against a totally broken registry where even
// the assigned asset is missing.
function resolveBackgroundUrl(
  driverNumber: number,
  requested: AssignedBackground,
): string {
  const requestedUrl = driverCardBackUrl(
    requested.bg.prefix,
    requested.variation,
  );
  if (requestedUrl) return requestedUrl;
  const assigned = pickAssignedBackground(driverNumber);
  if (import.meta.env.DEV) {
    console.warn(
      `card-backgrounds: missing asset for ${requested.bg.prefix}:${requested.variation}, ` +
        `falling back to assigned ${assigned.bg.prefix}:${assigned.variation} for driver #${driverNumber}`,
    );
  }
  return driverCardBackUrl(assigned.bg.prefix, assigned.variation) ?? "";
}

export function cardBackgroundUrl(driverNumber: number): string {
  const assigned = pickAssignedBackground(driverNumber);
  return resolveBackgroundUrl(driverNumber, assigned);
}

// The raw SVG asset URL for a card's background,
// resolved the same way useCardBgStyle resolves the source it rasterizes:
// an override wins when its asset exists, otherwise the driver's bijection-assigned background.
// The rasterization path turns this URL into a WebP blob URL;
// the SVG export reads it straight through to keep the background vector.
export function cardBackgroundSvgUrl(
  driverNumber: number,
  override?: { prefix: string; variation: number },
): string {
  if (override) {
    const url = driverCardBackUrl(override.prefix, override.variation);
    if (url) return url;
  }
  return cardBackgroundUrl(driverNumber);
}

export function cardBgStyle(driverNumber: number, tier?: string | null) {
  // Tone the art down to a subtle texture with a solid layer STACKED over it
  // (normal compositing), NOT `background-blend-mode` - which is broken on iOS
  // Safari, where the art then renders in full original colours. darken/lighten
  // against an opaque-ish black/white reduces exactly to this alpha composite
  // (Co = (1-a)*img for darken-vs-black), so the look is unchanged off iOS.
  const tone =
    tier === "white" ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.94)";
  return {
    "background-image": `linear-gradient(${tone}, ${tone}), url(${cardBackgroundUrl(driverNumber)})`,
    "background-size": "cover",
    "will-change": "transform",
  } as const;
}

export function useBgUrl(getUrl: Accessor<string>) {
  const [bgUrl, setBgUrl] = createSignal<string | undefined>(undefined);
  // Dedupe by URL. The effect re-fires whenever the parent passes a fresh
  // props.profile reference (refetch, route loader, etc.), but if the resolved
  // svgUrl is identical we must not tear down bgUrl - doing so flickers
  // bg.ready() and produces a phantom second card-slam mid-animation.
  let lastSvgUrl: string | undefined;
  createEffect(() => {
    const svgUrl = getUrl();
    if (svgUrl === lastSvgUrl) return;
    lastSvgUrl = svgUrl;
    setBgUrl(undefined);
    let cancelled = false;
    onCleanup(() => {
      cancelled = true;
    });
    getCardBgUrl(svgUrl)
      .then((url) => {
        if (!cancelled) setBgUrl(url);
      })
      .catch(() => {
        // Fallback: source SVG. Card still renders; jank returns for this one.
        if (!cancelled) setBgUrl(svgUrl);
      });
  });

  return bgUrl;
}

// Like cardBgStyle, but the background-image is the on-device-rasterized WebP
// bitmap instead of the raw SVG. Returns an accessor that yields `undefined`
// while the bitmap is loading (caller gates visibility on truthiness) and the
// blob URL once ready. See card-bg-cache for the rasterization + IDB pipeline.
// An explicit `override` bypasses the driver's bijection-assigned background and
// rasterizes the chosen catalog entry instead, falling back to the assigned one
// if that entry's asset is missing. This is the override path anticipated by
// resolveBackgroundUrl, used by the card lab to preview arbitrary backgrounds.
export function useCardBgStyle(
  driverNumber: Accessor<number>,
  tier: Accessor<string | null | undefined>,
  override?: Accessor<{ prefix: string; variation: number } | undefined>,
): {
  ready: Accessor<boolean>;
  style: Accessor<Record<string, string>>;
} {
  const bgUrl = useBgUrl(() => {
    const chosen = override?.();
    if (chosen) {
      const url = driverCardBackUrl(chosen.prefix, chosen.variation);
      if (url) return url;
    }
    return cardBackgroundUrl(driverNumber());
  });
  const style = (): Record<string, string> => {
    const url = bgUrl();
    // Tone the art down with a solid layer STACKED over it (normal compositing),
    // NOT `background-blend-mode` - broken on iOS Safari, where the art showed in
    // full original colours. darken/lighten against an opaque-ish tone is exactly
    // this alpha composite, so the look is unchanged off iOS. See cardBgStyle.
    const tone =
      tier() === "white" ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.94)";
    // Bottom layer: an opaque backstop in the tone's own color.
    // The art keeps its alpha and the tone layer is translucent,
    // so without a backstop the face stays see-through wherever the art is transparent,
    // and decoration stacked behind the card body (the tier-colored edge slabs) tints the whole face.
    const backstop = "#000";
    const base: Record<string, string> = {
      "background-image": url
        ? `linear-gradient(${tone}, ${tone}), url(${url}), linear-gradient(${backstop}, ${backstop})`
        : `linear-gradient(${tone}, ${tone}), linear-gradient(${backstop}, ${backstop})`,
      "background-size": "cover",
      "will-change": "transform",
    };
    return base;
  };
  return { ready: () => bgUrl() !== undefined, style };
}

// Pre-warm one driver's card background bitmap. Safe to call repeatedly;
// inflight + cached lookups are deduped. Cheap enough to fire on data arrival
// without gating.
export function prewarmCard(driverNumber: number): void {
  prewarmCardBg(cardBackgroundUrl(driverNumber));
}

// ── Page backdrop ──────────────────────────────────────────────────────────
// A second background drawn behind page content, faded out toward the top.
// Reuses the same SVG catalog + rasterization pipeline as the card background,
// picked at random once per browser session from the full catalog. The pick is
// the same for every viewer in a session and is intentionally not tied to their
// card. (The page strip renders every background fine - see the strip-cover +
// anchored-fade handling at the PageContainer call site - so there's no pool to
// curate against.)

// Stable per browser session: picked once at module load, survives client-side
// nav, reshuffles on full reload.
const SESSION_BACKDROP: AssignedBackground =
  FLAT[Math.floor(Math.random() * FLAT.length)];

export function pageBackdropUrl(): string {
  return (
    driverCardBackUrl(SESSION_BACKDROP.bg.prefix, SESSION_BACKDROP.variation) ??
    ""
  );
}

export function prewarmPageBackdrop(): void {
  const url = pageBackdropUrl();
  if (url) prewarmCardBg(url);
}

// Returns an accessor to the rasterized backdrop URL (undefined until ready).
// The backdrop is a session constant, so this resolves exactly once. The style
// stack is consumer-specific (page backdrop, OG image, etc.) so it lives at the
// call site, not here.
export function usePageBackdropUrl(): Accessor<string | undefined> {
  const [url, setUrl] = createSignal<string | undefined>(undefined);
  const svg = pageBackdropUrl();
  if (svg) {
    let cancelled = false;
    onCleanup(() => {
      cancelled = true;
    });
    getCardBgUrl(svg)
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch(() => {
        if (!cancelled) setUrl(svg);
      });
  }
  return url;
}

// One-shot orphan cleanup for the IDB bitmap cache. Drops entries for URLs
// that aren't in the current catalog glob - catches the deploy case where
// re-triage renamed or removed backgrounds, so old rasterizations don't
// accumulate across deploys. No-op in dev. Safe to call once per session.
export function gcCardBgCache(): void {
  if (typeof window === "undefined") return;
  const knownUrls = new Set<string>(Object.values(_backFiles));
  const run = () => void gcOrphans(knownUrls).catch(() => undefined);
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(run, { timeout: 5000 });
  } else {
    window.setTimeout(run, 1000);
  }
}
